import { HttpClient } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Router } from '@angular/router'
import { injectQuery, injectMutation, injectQueryClient } from './query'
import { queryKeys } from './query-keys'
import { ToastService } from './toast.service'
import { AgendaItemSubCategory } from './meetings.service'
import { MeetingProfileToBeCreated } from './support/meeting-profiles/support-meeting-profiles-new-page/support-meeting-profiles-new-page.component'

export type AgendaItemExample = {
  content: string
  subCategory: AgendaItemSubCategory
}

export type MeetingProfile = {
  id: number
  name: string
  clientId: number
  agendaItemExamples: Partial<Record<AgendaItemSubCategory, AgendaItemExample[]>>
  templateKey: string
}

export type MeetingProfileWithoutId = Omit<MeetingProfile, 'id'>

export type MeetingProfileWithClient = MeetingProfile & {
  client: { id: number; name: string }
}

export type MeetingProfileOption = Pick<MeetingProfile, 'id' | 'name'>

@Injectable({
  providedIn: 'root',
})
export class MeetingProfilesService {
  httpClient = inject(HttpClient)
  query = injectQuery()
  mutation = injectMutation()
  queryClient = injectQueryClient()
  toastService = inject(ToastService)
  router = inject(Router)

  getAllMeetingProfiles() {
    return this.query({
      queryKey: queryKeys.meetingProfiles.all,
      queryFn: () => this.httpClient.get<MeetingProfileWithClient[]>('/api/meeting-profiles'),
    }).result$
  }

  getOwnMeetingProfiles() {
    return this.query({
      queryKey: queryKeys.meetingProfiles.own,
      queryFn: () => this.httpClient.get<MeetingProfileOption[]>('/api/meeting-profiles/own'),
    }).result$
  }

  getMeetingProfilesOfClient(clientId: number) {
    return this.query({
      queryKey: queryKeys.meetingProfiles.byClientId(clientId),
      queryFn: () => this.httpClient.get<MeetingProfileWithClient[]>('/api/meeting-profiles', { params: { clientId } }),
    }).result$
  }

  getMeetingProfileById(id: number, receiveUpdates = true) {
    return this.query({
      queryKey: queryKeys.meetingProfiles.byId(id),
      queryFn: () => this.httpClient.get<MeetingProfileWithClient>(`/api/meeting-profiles/${id}`),
      refetchOnMount: receiveUpdates,
      refetchOnReconnect: receiveUpdates,
      refetchOnWindowFocus: receiveUpdates,
    }).result$
  }

  updateMeetingProfile() {
    return this.mutation({
      mutationFn: (meetingProfile: MeetingProfile) => this.httpClient.put<MeetingProfile>(`/api/meeting-profiles/${meetingProfile.id}`, meetingProfile),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetingProfiles.all })
        this.toastService.show('MEETING_PROFILES.UPDATED')
      },
      onError: (error) => {
        this.toastService.show('MEETING_PROFILES.UPDATE_FAILED')
      },
    })
  }
  createMeetingProfile() {
    return this.mutation({
      mutationFn: (meetingProfile: MeetingProfileToBeCreated) => this.httpClient.post<MeetingProfile>('/api/meeting-profiles', meetingProfile),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetingProfiles.all })
        this.toastService.show('MEETING_PROFILES.CREATED')
      },
      onError: (error) => {
        this.toastService.show('MEETING_PROFILES.CREATE_FAILED')
      },
    })
  }

  deleteMeetingProfile() {
    return this.mutation({
      mutationFn: (meetingProfileId: number) => this.httpClient.delete(`/api/meeting-profiles/${meetingProfileId}`),
      onSuccess: () => {
        this.queryClient.invalidateQueries({ queryKey: queryKeys.meetingProfiles.all })
        this.toastService.show('MEETING_PROFILES.DELETED')
      },
      onError: (error) => {
        this.toastService.show('MEETING_PROFILES.DELETE_FAILED')
      },
    })
  }
}
